import { useCallback, useEffect, useState } from "react";

type Area = "session" | "local";

function storage(area: Area): Storage | null {
  try {
    return area === "session" ? window.sessionStorage : window.localStorage;
  } catch {
    return null;
  }
}

function read(area: Area, key: string): Set<string> {
  try {
    const raw = storage(area)?.getItem(key);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(parsed) ? parsed.filter((v) => typeof v === "string") : []);
  } catch {
    return new Set();
  }
}

/**
 * A set of ids kept in browser storage: which notices this browser has put
 * away. A per-viewer convenience only. Storage may be missing or throw (a
 * private window), and then the set simply lives as long as the page.
 */
export function useStoredIds(area: Area, key: string) {
  const [ids, setIds] = useState<Set<string>>(() => read(area, key));
  // A new key is a new reader: start from what THEY put away.
  useEffect(() => {
    setIds(read(area, key));
  }, [area, key]);
  const add = useCallback(
    (id: string) => {
      setIds((prev) => {
        if (prev.has(id)) return prev;
        // Merged with what is stored now, not just this instance's copy:
        // the strip under the top bar and the popup in the layout keep one
        // key between them, and either would otherwise drop the other's.
        const next = new Set([...read(area, key), ...prev, id]);
        try {
          storage(area)?.setItem(key, JSON.stringify([...next]));
        } catch {
          // Not stored; still hidden for this page's life.
        }
        return next;
      });
    },
    [area, key],
  );
  return { ids, add };
}

/** The prefix every account's session-long notice dismissals share. */
export const DISMISSED_PREFIX = "cmfis.notices.dismissed.";

/**
 * The prefix of the popups an account has answered with „Rozumím", in
 * localStorage: kept across logins, unlike a dismissal.
 */
export const ANSWERED_PREFIX = "cmfis.notices.answered.";

/**
 * Forgets every notice closed in this tab. Called on a successful login:
 * a closed lišta or popup is put away for one login, and the tab outlives
 * the logout that ends it.
 */
export function forgetDismissedNotices() {
  try {
    const store = window.sessionStorage;
    const keys: string[] = [];
    for (let i = 0; i < store.length; i++) {
      const k = store.key(i);
      if (k?.startsWith(DISMISSED_PREFIX)) keys.push(k);
    }
    keys.forEach((k) => store.removeItem(k));
  } catch {
    // No storage, nothing remembered.
  }
}
